/**
 * `SCR-CLIENTNEW` — P-CLIENT-01, at the screen. OWNER: web-frontend-engineer.
 *
 * LAW 31: "Paul needs to be able to set up client profiles… create a record for
 * each client that has all their information including shipping address etc."
 *
 * THE SHAPE OF THIS SCREEN IS AN ARGUMENT. One field is required — his name —
 * and everything else can be added later, because the room this runs in is an
 * office with a man standing in his shirtsleeves and the tailor has four
 * minutes. A form that demands an address before it will remember who somebody
 * is, is a form he fills in afterwards from memory, which is how a CRM becomes
 * a filing cabinet nobody opens.
 *
 * TWO THINGS IT SAYS OUT LOUD rather than implying with a blank:
 *   · an email means only that he COULD be given a way in one day. There is no
 *     client portal, nothing is sent, and the hint says so — a screen that
 *     collects an address in silence is a screen a tailor assumes emailed him.
 *   · the key. `P-ROAD-IDEM-01`: the key is generated ONCE when the screen
 *     opens and sent with the write, so a second tap on a hotel connection
 *     returns the same man rather than a second record with a second client
 *     reference that can never be reused.
 */

import { useRef, useState } from 'react';
import { t } from '@pt/copy';
import { api } from '../../lib/api.ts';
import { useSubmission } from '../../lib/resource.ts';
import { AppShell } from '../../app/AppShell.tsx';
import { useNavigation } from '../../app/Router.tsx';
import { Button, Statement, TextInput } from '../../components/primitives.tsx';
import { RefusalStatement } from '../../components/backoffice.tsx';
import { OfflineStatement } from '../../components/states.tsx';

export function ClientNewScreen() {
  const { navigate } = useNavigation();
  const submission = useSubmission();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [contact, setContact] = useState('');
  const [made, setMade] = useState<{ clientId: string; portalStatement: string } | null>(null);

  /* Generated ONCE, when the screen opens — which is what makes it a key to
     THIS act rather than to each attempt at it. */
  const key = useRef(`client-new-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const submit = () =>
    void submission.run(async () => {
      const created = await api.createClient({
        displayName: displayName.trim(),
        ...(email.trim() === '' ? {} : { email: email.trim() }),
        ...(deliveryAddress.trim() === '' ? {} : { deliveryAddress: deliveryAddress.trim() }),
        ...(contact.trim() === '' ? {} : { contact: contact.trim() }),
        idempotencyKey: key.current,
      });
      setMade({ clientId: created.clientId, portalStatement: created.portalStatement });
      return created;
    });

  if (made !== null) {
    return (
      <AppShell title={t('clientnew.heading')}>
        <Statement>{t('clientnew.done.heading', { clientNameStaff: displayName.trim() })}</Statement>
        {/* The server's own sentence about the portal, not a second copy of it. */}
        <Statement>{made.portalStatement}</Statement>
        <div className="pt-actions">
          <Button onClick={() => navigate(`/atelier/clients/${made.clientId}`)}>
            {t('clientnew.open.action')}
          </Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title={t('clientnew.heading')}>
      <Statement>{t('clientnew.lede')}</Statement>

      <TextInput
        id="pt-clientnew-name"
        label={t('clientnew.name.label')}
        value={displayName}
        onChange={setDisplayName}
        autoComplete="off"
      />
      <TextInput
        id="pt-clientnew-email"
        label={t('clientnew.email.label')}
        value={email}
        onChange={setEmail}
        type="email"
        autoComplete="off"
        hint={t('clientnew.email.help')}
      />
      <TextInput
        id="pt-clientnew-address"
        label={t('clientnew.address.label')}
        value={deliveryAddress}
        onChange={setDeliveryAddress}
        multiline
        autoComplete="off"
      />
      <TextInput
        id="pt-clientnew-contact"
        label={t('clientnew.contact.label')}
        value={contact}
        onChange={setContact}
        autoComplete="off"
      />

      {submission.rejection === null ? null : <RefusalStatement rejection={submission.rejection} />}
      {submission.offline ? <OfflineStatement /> : null}

      <div className="pt-actions">
        <Button onClick={submit} busy={submission.busy}>
          {t('clientnew.submit')}
        </Button>
      </div>
    </AppShell>
  );
}
